<template>
  <vue-dropzone
    ref="dropzone"
    id="dropzone"
    :useCustomSlot="true"
    :include-styling="false"
    @vdropzone-success="onSuccess"
    @vdropzone-error="onError"
    @vdropzone-file-added="onFileAdded"
    @vdropzone-files-added="onFilesAdded"
    v-on="{ ...events }"
    :options="dropzoneOptions"
    :style="{ pointerEvents: disabled ? 'none' : 'auto' }"
    :class="{ 'dropzone-box': withDropzoneBox, 'dropzone-box-without-previews': dropzoneOptions.disablePreviews }"
  >
    <v-row no-gutters>
      <slot name="uploadContent">
        <v-col cols="12" :class="{ 'pa-7': imagePreviewUrl }">
          <template v-if="imagePreviewUrl">
            <div style="position: relative; display: inline-block">
              <img :src="imagePreviewUrl" />
              <div class="file-input-remove-preview">
                <v-icon @click="removeAllFiles" color="white">$closeError</v-icon>
              </div>
            </div>
          </template>
          <template v-else>
            <v-row v-if="withDropzoneBox" no-gutters justify="center" class="pt-4 pb-3">
              <v-icon size="26">mdi-tray-arrow-up</v-icon>
            </v-row>
            <v-row no-gutters :class="{ 'justify-center': withDropzoneBox }">
              <template v-if="withDropzoneBox">
                <span class="text-body-2 px-2">
                  <i18n path="dropzone.dropDownTitle" tag="span" style="font-size: 12px">
                    <template #value>
                      <span class="font-italic font-weight-medium primary--text" style="cursor: pointer">
                        {{ dropzoneTitle }}
                      </span>
                    </template>
                  </i18n>
                </span>
              </template>
              <template v-else>
                <base-button class="tw:mb-2" :loading="loading" :disabled="disabled" :icon="buttonIcon">
                  {{ $i18n.t(buttonTranslationPath) }}
                </base-button>
              </template>
            </v-row>
          </template>
        </v-col>
      </slot>
    </v-row>
    <template v-if="!imagePreviewUrl">
      <v-row
        v-for="(subheader, idx) of buttonSubheaders"
        :key="idx"
        no-gutters
        class="upload-subheader"
        :class="{ 'justify-center': withDropzoneBox, 'pt-2': withDropzoneBox }"
      >
        {{ subheader.type === 'path' ? $i18n.t(subheader.value) : subheader.value }}
      </v-row>
    </template>
  </vue-dropzone>
</template>

<script>
import { get } from 'vuex-pathify';

import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';

export default {
  name: 'FileInput',
  components: {
    vueDropzone: vue2Dropzone,
  },
  inheritAttrs: false,
  props: {
    options: {
      type: Object,
      required: true,
    },
    events: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    expandedFilesExtensions: {
      type: Array,
      required: false,
    },
    buttonSubheaders: {
      type: Array,
      default: () => [],
    },
    buttonIcon: {
      type: String,
      default: 'mdi-upload',
    },
    buttonTranslationPath: {
      type: String,
      default: 'admin.selectFile',
    },
    hideErrors: {
      type: Array,
      default: () => {
        return [];
      },
    },
    withDropzoneBox: {
      type: Boolean,
      default: false,
    },
    imagePreviewUrl: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    loading: false,
    filesQueued: false,
  }),
  computed: {
    token: get('authentication/token'),
    dropzoneTitle() {
      return this.$i18n.t(
        `dropzone.${this.options?.maxFiles > 1 ? 'dropDownTitleValueMultiple' : 'dropDownTitleValue'}`
      );
    },
    dropzoneOptions() {
      const self = this;
      const multipleSuffix = this.options.uploadMultiple ? 'multiple' : '';
      return {
        ...{
          method: 'post',
          timeout: 0,
          thumbnailWidth: 150,
          dictDefaultMessage: 'dictDefaultMessage',
          dictFallbackMessage: 'dictFallbackMessage',
          dictFallbackText: 'dictFallbackText',
          dictFileTooBig: 'dictFileTooBig',
          dictInvalidFileType: 'dictInvalidFileType',
          dictResponseError: 'dictResponseError',
          dictCancelUpload: '',
          dictUploadCanceled: 'dictUploadCanceled',
          dictRemoveFile: '<i class="v-icon mdi mdi-delete theme--light" style="font-size: 16px"></i>',
          dictMaxFilesExceeded: 'dictMaxFilesExceeded',
          dictFileSizeUnits: 'dictFileSizeUnits',
          maxFilesize: null,
          headers: {
            'X-Access-Token': this.token,
          },
          [`sending${multipleSuffix}`]: e => {
            self.loading = true;
            self.$emit('loading', e);
          },
          [`complete${multipleSuffix}`]: () => {
            self.loading = false;
          },
          [`error${multipleSuffix}`]: (file, response) => {
            if (['dictInvalidFileType', 'dictMaxFilesExceeded', 'dictFileTooBig'].includes(response)) {
              const fileToDelete = Array.isArray(file) ? file[0] : file;
              self.$refs.dropzone.removeFile(fileToDelete);
            }
            if (!this.hideErrors.includes(response)) {
              const errorDefinition = {};
              if (typeof response === 'string') {
                errorDefinition.message = this.$i18n.t(`errors.dropzone.${response}`);
              } else {
                Object.assign(errorDefinition, this.$_getErrorObject(response));
              }
              self.$store.set('snackbar/PUSH_ERROR!', errorDefinition);
            }
            self.$emit('error', { file, response });
          },
        },
        ...this.options,
      };
    },
  },
  methods: {
    onSuccess(file, response) {
      this.$emit('successfullyAdded', file, response);
    },
    onError(file, msg, xhr) {
      if (xhr) this.removeAllFiles();
      this.$emit('error');
    },
    onFileAdded(file) {
      if (this.filesQueued) {
        for (let acceptedFile of this.$refs.dropzone.getAcceptedFiles()) {
          this.$refs.dropzone.removeFile(acceptedFile);
        }
        this.filesQueued = false;
      }
      this.$emit('fileAdded', file);
    },
    onFilesAdded(files) {
      if (!this.expandedFilesExtensions) {
        return;
      }
      const addedFiles = [];
      const addedExts = [];
      let addingFilesLength = 0;
      for (let file of files) {
        addingFilesLength++;
        addedFiles.push(file);
        addedExts.push(file.name.split('.').pop());
      }
      if (addedFiles.length < 1) {
        return;
      }
      if (this.$refs.dropzone.options.maxFiles && addingFilesLength > this.$refs.dropzone.options.maxFiles) {
        this.removeAllFiles();
        return;
      }
      const ext = addedFiles[0].name.split('.').pop();
      for (let eFE of this.expandedFilesExtensions) {
        if (eFE.exts.includes(ext)) {
          if (addedFiles.length < eFE.minFiles || addedFiles.length > eFE.maxFiles) {
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('errors.dropzone.invalidNumberOfFiles', {
                ext: eFE.name,
              }),
            });
            this.removeAllFiles();
          } else if (!eFE.canExtDuplicate && new Set(addedExts).size !== addedExts.length) {
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: 'dropzone.duplicateFileExtention',
            });
            this.removeAllFiles();
          } else if (addedExts.filter(addedExt => eFE.exts.includes(addedExt)).length !== addedExts.length) {
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('errors.dropzone.invalidFileExtention', {
                ext: eFE.name,
              }),
            });
            this.removeAllFiles();
          } else {
            this.$refs.dropzone.setOption('url', eFE.url);
            this.$emit('expandedFilesExtensionDetected', eFE);
            this.filesQueued = true;
          }
          break;
        }
      }
    },
    removeAllFiles() {
      this.$refs.dropzone.removeAllFiles();
    },
  },
};
</script>

<style lang="scss">
.dz-image,
.dz-progress,
.dz-error-message,
.dz-success-mark,
.dz-error-mark,
.dz-size {
  display: none;
}
.dz-filename {
  max-height: 22px;
  max-width: 160px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dz-preview {
  width: 100%;
  height: 22px;
  float: left;
}
.dz-details {
  max-width: 80%;
  float: left;
  overflow: hidden;
}
.dz-remove {
  float: left;
  i {
    padding-bottom: 3px;
    padding-left: 10px;
  }
}
.upload-subheader {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 9px;
  line-height: 11px;
  overflow: hidden;
  text-align: left;
  color: rgba(0, 0, 0, 0.59);
}
.dz-drag-hover {
  background-color: map-get($colors, primarybackground);
  border: 1px solid map-get($colors, primary) !important;
}
.dropzone-box {
  border-radius: 8px;
  border: 1px dashed #ccc;
  .dz-message {
    width: 100%;
    min-height: 120px;
  }
}
.dropzone-box-without-previews {
  .dz-preview {
    display: none;
  }
}
.file-input-remove-preview {
  position: absolute;
  top: -10px;
  right: -12px;
}
</style>
